import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMoney, STATUS_COLORS } from "@/lib/pharmacy";
import { CheckCircle2, Mail } from "lucide-react";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/orders/by-number/${orderNumber}`)
      .then((r) => setO(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Order not found"));
  }, [orderNumber]);

  if (err) return <div className="blz-container py-20 text-center text-slate-500">{err}</div>;
  if (!o) return <div className="blz-container py-20 text-slate-500">Loading...</div>;

  return (
    <div className="blz-container py-14">
      <div className="max-w-2xl mx-auto text-center">
        <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto"/>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-3">Thank you, your order is received</h1>
        <p className="text-slate-500 mt-2">A confirmation email has been sent to <span className="font-mono">{o.shipping.email}</span>.</p>
        <div className="mt-5 inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2 font-mono text-sm" data-testid="order-number">
          Order <strong>{o.order_number}</strong> <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[o.status] || "bg-slate-100 text-slate-700"}`}>{o.status}</span>
        </div>
        {o.payment_method && (
          <p className="text-sm text-slate-600 mt-4">We'll follow up at <span className="font-mono">{o.shipping.email}</span> with instructions for your chosen payment method: <strong>{o.payment_method}</strong>.</p>
        )}
      </div>

      <div className="mt-10 max-w-3xl mx-auto card-flat p-6">
        <h3 className="font-display font-bold text-lg mb-4">Order details</h3>
        <ul className="divide-y divide-slate-100">
          {o.items.map((i) => (
            <li key={`${i.product_id}-${i.pack_size}`} className="py-3 flex justify-between">
              <div>
                <div className="font-semibold text-slate-900">{i.name}</div>
                <div className="text-xs text-slate-500">{i.generic_name} · {i.strength} · Pack of {i.pack_size} × {i.quantity}</div>
              </div>
              <div className="font-mono text-sm text-slate-900">{fmtMoney(i.line_total)}</div>
            </li>
          ))}
        </ul>
        <div className="mt-4 space-y-1 text-sm">
          <div className="flex justify-between"><span>Total pills</span><span className="font-mono">{o.total_pills}</span></div>
          <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{fmtMoney(o.subtotal)}</span></div>
          <div className="flex justify-between"><span>Discount</span><span className="font-mono">-{fmtMoney(o.discount)}</span></div>
          <div className="flex justify-between"><span>Shipping</span><span className="font-mono">{fmtMoney(o.shipping_cost)}</span></div>
          <div className="flex justify-between font-bold text-slate-900 pt-2 border-t border-slate-100"><span>Grand total</span><span className="font-mono">{fmtMoney(o.grand_total)}</span></div>
        </div>
        <div className="mt-6 flex gap-3 justify-center">
          <Link to="/catalogue" className="btn-ghost">Continue shopping</Link>
          <Link to={`/my-orders/${o.order_number}`} className="btn-primary"><Mail className="h-4 w-4"/> Track order</Link>
        </div>
      </div>
    </div>
  );
}
